// ─── Servicio de configuración y cálculo de honorarios médicos ─────────────
// Modelo mixto configurable: porcentaje por tipo de prestación y/o monto fijo por acto.
// Colecciones: honorarioConfig (reglas por profesional), honorarioContratos (contratos).
//
// REGLAS DE NEGOCIO QUE IMPLEMENTA ESTA VERSIÓN
//  1. Un ítem solo se liquida a un profesional si se le puede atribuir de forma
//     explícita (professionalId en el ítem / venta, o la cita del profesional).
//     La existencia de una regla de contrato NO implica que el acto sea suyo.
//  2. El período de un acto es la fecha del acto: fecha de la cita (bookings),
//     fecha de la cirugía (honorarios quirúrgicos) o fecha de pago (ventas sueltas).
//  3. Los honorarios médicos de cirugía se liquidan SOLO desde la cirugía, una vez
//     por ítem de presupuesto, y solo el ítem que corresponde al rol del médico.
//  4. Nada se descarta en silencio: actos sin regla, sin protocolo o sin pago
//     aparecen con honorario 0 y el motivo; los ítems sin profesional asignado que
//     calzan con un contrato van a `pendientesRevision`; los errores de lectura van
//     a `advertencias`.

import {
  collection, doc, getDoc, getDocs, query, where,
  setDoc, addDoc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { getCollection } from './firestoreService';

// ─── Constantes ─────────────────────────────────────────────────────────────

const TZ = 'America/Santiago';
const CONTRATOS_COLLECTION = 'honorarioContratos';
const AJUSTES_COLLECTION = 'honorarioAjustes';
const LIQUIDACIONES_COLLECTION = 'honorarioLiquidaciones';

const ESTADOS_VENTA_ANULADA = ['anulada', 'anulado', 'nula', 'cancelada', 'cancelado'];
const ESTADOS_VENTA_PENDIENTE = ['pendiente', 'por cobrar', 'credito', 'borrador'];
const ESTADOS_PRESUPUESTO_PAGADO = ['cobrado', 'pagado'];
const ESTADOS_CIRUGIA_CANCELADA = ['cancelada', 'cancelado', 'cancelled', 'canceled', 'anulada', 'suspendida'];
const ESTADOS_BOOKING_NO_REALIZADO = ['cancelled', 'canceled', 'cancelada', 'cancelado', 'no_show', 'noshow', 'no-show', 'ausente'];
const ESTADOS_FICHA_BORRADOR = ['borrador', 'draft'];

const OPCIONES_DEFAULT = {
  // true: el honorario quirúrgico solo se paga si el presupuesto está cobrado/pagado.
  exigirPagoEnCirugias: false,
  // true: en modalidad 'fijo'/'mixto' el monto fijo se multiplica por la cantidad.
  fijoPorCantidad: false,
  // Días después del acto en que aún se acepta la ficha SOAP (fichas completadas al día siguiente).
  toleranciaDiasSOAP: 1,
  // Reparte descuentos globales de la venta/presupuesto proporcionalmente entre los ítems.
  prorratearDescuentos: true,
  // true: si el ítem y la venta no traen profesional, usar el profesional del presupuesto.
  atribuirPorPresupuesto: false,
};

// ─── Utilidades ─────────────────────────────────────────────────────────────

function normalizeStr(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function limpiarNombre(s) {
  return normalizeStr(s).replace(/^(dra?|doctora?)\.?\s+/, '');
}

function txt(v) {
  if (typeof v === 'string') return v.trim();
  return v ? String(v) : '';
}

/** Convierte números o strings ("15000", "15.000", "$ 15.000") a número. */
function toNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const limpio = v
      .replace(/[$\s]/g, '')
      .replace(/\.(?=\d{3}(\D|$))/g, '') // separador de miles
      .replace(',', '.');
    const n = Number(limpio);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

const fmtFecha = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit',
});

function esFechaISO(s) {
  return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
}

/**
 * Normaliza cualquier fecha a 'YYYY-MM-DD' en hora de Chile.
 * Los strings 'YYYY-MM-DD' se devuelven tal cual (antes se desplazaban un día
 * porque new Date('YYYY-MM-DD') se interpreta en UTC).
 */
function toDateStr(value) {
  if (!value) return '';
  if (esFechaISO(value)) return value;
  let d;
  if (typeof value.toDate === 'function') d = value.toDate();
  else if (value instanceof Date) d = value;
  else if (typeof value === 'object' && typeof value.seconds === 'number') d = new Date(value.seconds * 1000);
  else d = new Date(value);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return fmtFecha.format(d);
}

function addDays(fecha, n) {
  const [y, m, d] = fecha.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

function enRango(f, desde, hasta) {
  return !!f && f >= desde && f <= hasta;
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function idProfesionalDe(obj) {
  if (!obj) return '';
  return obj.professionalId || obj.profesionalId || obj.vetId || '';
}

function cantidadItem(item) {
  const c = toNumber(item?.cantidad ?? item?.quantity);
  return c > 0 ? c : 1;
}

function montoItem(item) {
  if (!item) return 0;
  const total = toNumber(item.total);
  if (total) return total;
  return toNumber(item.precio ?? item.price) * cantidadItem(item);
}

/** Factor para repartir descuentos globales (totalFinal menor que la suma de ítems). */
function factorDescuento(docConTotal, items) {
  const suma = (items || []).reduce((s, i) => s + montoItem(i), 0);
  const final = toNumber(docConTotal?.totalFinal);
  if (!suma || !final || final >= suma) return 1;
  return final / suma;
}

const CONDICIONES_DEFAULT = {
  descontarIVA: false,
  tasaIVA: 19,
  descontarDescuentosComerciales: false,
  prorratearDescuentos: true,
  exigirPagoEnCirugias: false,
  garantiaMensual: 0,
};

function normalizeCondiciones(condiciones) {
  const c = condiciones || {};
  return {
    descontarIVA: c.descontarIVA === true,
    tasaIVA: toNumber(c.tasaIVA) || 19,
    descontarDescuentosComerciales: c.descontarDescuentosComerciales === true,
    prorratearDescuentos: c.prorratearDescuentos !== false,
    exigirPagoEnCirugias: c.exigirPagoEnCirugias === true,
    garantiaMensual: toNumber(c.garantiaMensual),
  };
}

function tasaIVAContrato(presupuesto, condiciones) {
  if (condiciones?.tasaIVA) return toNumber(condiciones.tasaIVA) / 100;
  if (presupuesto && toNumber(presupuesto.iva) && toNumber(presupuesto.subtotal)) {
    return toNumber(presupuesto.iva) / toNumber(presupuesto.subtotal);
  }
  return 0.19;
}

function montoBaseItem(monto, rule, presupuesto) {
  const condiciones = rule?._condiciones || CONDICIONES_DEFAULT;
  let base = toNumber(monto);
  if (condiciones.descontarIVA) {
    const tasa = tasaIVAContrato(presupuesto, condiciones);
    base = base / (1 + tasa);
  }
  if (condiciones.descontarDescuentosComerciales && presupuesto) {
    const subtotal = toNumber(presupuesto.subtotal);
    const descuento = toNumber(presupuesto.descuentoTotal);
    if (subtotal && descuento && descuento < subtotal) {
      base *= (subtotal - descuento) / subtotal;
    }
  }
  return base;
}

// ─── Configuración de reglas de honorario ──────────────────────────────────

function normalizarRegla(r) {
  if (!r) return null;
  return {
    ...r,
    modalidad: r.modalidad || 'porcentaje',
    porcentaje: toNumber(r.porcentaje),
    montoFijo: toNumber(r.montoFijo),
  };
}

/**
 * Obtiene la configuración de honorarios de un profesional.
 * @returns {Promise<{ professionalId, rules: Array, updatedAt }>}
 */
export async function getHonorarioConfig(professionalId) {
  if (!professionalId) return { professionalId: '', rules: [], updatedAt: null };
  const snap = await getDoc(doc(db, 'honorarioConfig', professionalId));
  if (!snap.exists()) return { professionalId, rules: [], updatedAt: null };
  return { professionalId, ...snap.data() };
}

export async function saveHonorarioConfig(professionalId, rules) {
  if (!professionalId) throw new Error('professionalId es requerido');
  await setDoc(doc(db, 'honorarioConfig', professionalId), {
    professionalId,
    rules: (rules || []).map(normalizarRegla),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return getHonorarioConfig(professionalId);
}

// ─── Cálculo de un ítem ─────────────────────────────────────────────────────

/**
 * Calcula el honorario de un ítem según la regla.
 * @param {number|string} montoCobrado
 * @param {object} rule - { modalidad, porcentaje, montoFijo }
 * @param {object} [opts] - { cantidad, fijoPorCantidad }
 */
export function calcularHonorarioItem(montoCobrado, rule, { cantidad = 1, fijoPorCantidad = false } = {}) {
  if (!rule) return 0;
  const monto = toNumber(montoCobrado);
  const pct = toNumber(rule.porcentaje);
  const fijoUnit = toNumber(rule.montoFijo);
  const fijo = fijoPorCantidad ? fijoUnit * (toNumber(cantidad) > 0 ? toNumber(cantidad) : 1) : fijoUnit;
  switch (rule.modalidad) {
    case 'porcentaje': return Math.round(monto * (pct / 100));
    case 'fijo': return Math.round(fijo);
    case 'mixto': return Math.round(monto * (pct / 100) + fijo);
    default: return 0;
  }
}

/**
 * Regla de honorarioConfig: primero la específica del tipo; si no, solo una regla
 * explícitamente general ('all'). Una regla con tipoPrestacionId vacío ya NO actúa
 * como comodín.
 */
function findRule(rules, tipoPrestacionId) {
  if (!rules?.length) return null;
  const especifica = tipoPrestacionId
    ? rules.find((r) => r.tipoPrestacionId && r.tipoPrestacionId === tipoPrestacionId)
    : null;
  const general = rules.find((r) => r.tipoPrestacionId === 'all');
  const r = especifica || general;
  return r ? { ...normalizarRegla(r), _generica: !especifica } : null;
}

// ─── Contratos ──────────────────────────────────────────────────────────────

export async function getHonorarioContratos(professionalId = '', status = '') {
  const ref = collection(db, CONTRATOS_COLLECTION);
  const conditions = [];
  if (professionalId) conditions.push(where('professionalId', '==', professionalId));
  if (status) conditions.push(where('status', '==', status));
  const snap = await getDocs(conditions.length ? query(ref, ...conditions) : ref);
  const contratos = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  // Los eliminados (borrado lógico) solo se devuelven si se piden explícitamente.
  return status === 'eliminado' ? contratos : contratos.filter((c) => c.status !== 'eliminado');
}

export async function getContratoById(id) {
  if (!id) return null;
  const snap = await getDoc(doc(db, CONTRATOS_COLLECTION, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

function normalizeServicios(services) {
  return (services || []).map((s) => ({
    arancelId: s.arancelId || '',
    prestacion: s.prestacion || '',
    tipoPrestacionId: s.tipoPrestacionId || '',
    tipoPrestacionName: s.tipoPrestacionName || '',
    modalidad: s.modalidad || 'porcentaje',
    porcentaje: toNumber(s.porcentaje),
    montoFijo: toNumber(s.montoFijo),
  }));
}

/**
 * En modo parcial solo normaliza los campos presentes: una actualización que no
 * trae `services` o `status` ya no los borra ni los resetea.
 */
function normalizeContratoData(data, { parcial = false } = {}) {
  const out = { ...data };
  delete out.id;
  delete out.createdAt;
  if (!parcial || 'status' in data) out.status = data.status || 'pendiente';
  if (!parcial || 'active' in data) out.active = data.active !== false;
  if (!parcial || 'services' in data) out.services = normalizeServicios(data.services);
  if (!parcial || 'condiciones' in data) out.condiciones = normalizeCondiciones(data.condiciones);
  Object.keys(out).forEach((k) => { if (out[k] === undefined) delete out[k]; });
  out.updatedAt = serverTimestamp();
  return out;
}

export async function createHonorarioContrato(data) {
  if (!data?.professionalId) throw new Error('professionalId es requerido');
  const payload = normalizeContratoData(data);
  payload.createdAt = serverTimestamp();
  const ref = await addDoc(collection(db, CONTRATOS_COLLECTION), payload);
  return { id: ref.id, ...payload };
}

export async function updateHonorarioContrato(id, data) {
  if (!id) throw new Error('id es requerido');
  await updateDoc(doc(db, CONTRATOS_COLLECTION, id), normalizeContratoData(data, { parcial: true }));
  return getContratoById(id);
}

function rangosSeSolapan(a, b) {
  const aDesde = toDateStr(a.validFrom) || '0000-00-00';
  const aHasta = toDateStr(a.validTo) || '9999-12-31';
  const bDesde = toDateStr(b.validFrom) || '0000-00-00';
  const bHasta = toDateStr(b.validTo) || '9999-12-31';
  return aDesde <= bHasta && bDesde <= aHasta;
}

export async function acceptHonorarioContrato(id, userId = '') {
  const contrato = await getContratoById(id);
  if (!contrato) throw new Error('El contrato no existe');
  if (contrato.status !== 'pendiente') {
    throw new Error(`Solo se pueden aceptar contratos pendientes (estado actual: ${contrato.status})`);
  }

  // Evita dos contratos vigentes que compitan por los mismos actos.
  const vigentes = await getHonorarioContratos(contrato.professionalId, 'vigente');
  const conflicto = vigentes.find((c) => c.id !== id
    && (c.specialtyId || '') === (contrato.specialtyId || '')
    && (c.branchId || '') === (contrato.branchId || '')
    && (c.clinicId || '') === (contrato.clinicId || '')
    && c.active !== false
    && rangosSeSolapan(c, contrato));
  if (conflicto) {
    throw new Error(`Ya existe un contrato vigente que se solapa en fechas y alcance (${conflicto.id}). Cierre su vigencia antes de aceptar este.`);
  }

  await updateDoc(doc(db, CONTRATOS_COLLECTION, id), {
    status: 'vigente',
    acceptedAt: serverTimestamp(),
    acceptedBy: userId,
    updatedAt: serverTimestamp(),
  });
  return getContratoById(id);
}

/**
 * Por defecto hace borrado lógico para no alterar liquidaciones históricas.
 * Usar { definitivo: true } solo para contratos creados por error y nunca usados.
 */
export async function deleteHonorarioContrato(id, { definitivo = false, userId = '' } = {}) {
  const ref = doc(db, CONTRATOS_COLLECTION, id);
  if (definitivo) {
    await deleteDoc(ref);
    return;
  }
  await updateDoc(ref, {
    status: 'eliminado',
    active: false,
    deletedAt: serverTimestamp(),
    deletedBy: userId,
    updatedAt: serverTimestamp(),
  });
}

function contratoVigente(contrato, fecha = '') {
  if (!contrato || contrato.active === false) return false;
  if (!fecha) return true;
  const desde = toDateStr(contrato.validFrom);
  const hasta = toDateStr(contrato.validTo);
  if (desde && fecha < desde) return false;
  if (hasta && fecha > hasta) return false;
  return true;
}

// ─── Búsqueda de regla para un acto ────────────────────────────────────────

/**
 * Puntaje de coincidencia entre un servicio del contrato y el acto.
 * -1 = incompatible. Solo coincidencias exactas (normalizadas); se eliminó el
 * "una contiene a la otra", que hacía calzar "Consulta" con cualquier consulta.
 */
function puntajeServicio(s, act) {
  if (s.arancelId && act.arancelId) return s.arancelId === act.arancelId ? 100 : -1;
  if (s.tipoPrestacionId && act.tipoPrestacionId) {
    return s.tipoPrestacionId === act.tipoPrestacionId ? 60 : -1;
  }
  if (s.prestacion && act.prestacion && normalizeStr(s.prestacion) === normalizeStr(act.prestacion)) return 50;
  if (s.tipoPrestacionName && act.tipoPrestacionName
    && normalizeStr(s.tipoPrestacionName) === normalizeStr(act.tipoPrestacionName)) return 40;
  // Fallback: coincidencia parcial por prestación o tipo de prestación.
  if (s.prestacion && act.prestacion) {
    const sP = normalizeStr(s.prestacion);
    const aP = normalizeStr(act.prestacion);
    if (sP.includes(aP) || aP.includes(sP)) return 25;
  }
  if (s.tipoPrestacionName && act.tipoPrestacionName) {
    const sT = normalizeStr(s.tipoPrestacionName);
    const aT = normalizeStr(act.tipoPrestacionName);
    if (sT.includes(aT) || aT.includes(sT)) return 15;
  }
  return -1;
}

function filtrosContratoCompatibles(c, act) {
  if (c.branchId && act.branchId && c.branchId !== act.branchId) return false;
  if (c.clinicId && act.clinicId && c.clinicId !== act.clinicId) return false;
  if (c.specialtyId && act.specialtyId && c.specialtyId !== act.specialtyId) return false;
  return true;
}

/**
 * Elige la mejor regla entre todos los contratos vigentes (la más específica;
 * a igualdad, la del contrato con vigencia más reciente). Si ningún contrato
 * aplica, usa honorarioConfig.
 */
function findRuleForAct(contratos, configRules, act) {
  let mejor = null;
  for (const c of contratos || []) {
    if (c.status !== 'vigente' || !contratoVigente(c, act.date)) continue;
    if (!filtrosContratoCompatibles(c, act)) continue;
    const bonus = ['branchId', 'clinicId', 'specialtyId']
      .reduce((b, k) => b + (c[k] && act[k] && c[k] === act[k] ? 5 : 0), 0);
    const desde = toDateStr(c.validFrom);
    for (const s of c.services || []) {
      const p = puntajeServicio(s, act);
      if (p < 0) continue;
      const score = p + bonus;
      if (!mejor || score > mejor.score || (score === mejor.score && desde > mejor.desde)) {
        mejor = {
          score,
          desde,
          regla: {
            ...normalizarRegla(s),
            _contratoId: c.id,
            _condiciones: normalizeCondiciones(c.condiciones),
            _origen: 'contrato',
            specialtyName: c.specialtyName || '',
            specialtyId: c.specialtyId || '',
            clinicName: c.clinicName || '',
            branchName: c.branchName || '',
          },
        };
      }
    }
  }
  if (mejor) return mejor.regla;
  const r = findRule(configRules, act.tipoPrestacionId || '');
  return r ? { ...r, _contratoId: '', _origen: 'config' } : null;
}

// ─── Clasificación de actos ─────────────────────────────────────────────────

function esConsulta(tipoName) {
  const n = normalizeStr(tipoName);
  // Acepta "Consulta médica", "Consulta de especialidad", "Consulta especialidad", etc.
  return /\bconsulta\b/.test(n) && (/\bmedic/.test(n) || /\bespecialidad/.test(n));
}

function esHonorarioMedico(tipoName) {
  const n = normalizeStr(tipoName);
  return n.includes('honorario') && n.includes('medic');
}

function tipoDeItem(item) {
  return item?.tipoPrestacion || item?.tipoPrestacionName || item?.prestacion || item?.descripcion || '';
}

/** Rol al que corresponde un ítem de honorario del presupuesto. */
function rolDeItemHonorario(item) {
  const r = normalizeStr(item.rol || item.role || '');
  if (r.includes('anest')) return 'anestesia';
  if (r.includes('ciruj')) return 'cirujano';
  const texto = normalizeStr(`${item.prestacion || ''} ${item.descripcion || ''} ${item.tipoPrestacion || ''}`);
  return texto.includes('anestes') ? 'anestesia' : 'cirujano';
}

function nombresProfesional(p) {
  const set = new Set();
  if (!p) return set;
  [p.fullName, `${p.nombre || ''} ${p.apellidos || ''}`, p.displayName, p.name]
    .map(limpiarNombre)
    .filter(Boolean)
    .forEach((n) => set.add(n));
  return set;
}

function buildProfNameMap(professionals) {
  const map = {};
  (professionals || []).forEach((p) => {
    nombresProfesional(p).forEach((key) => {
      if (!map[key]) map[key] = new Set();
      map[key].add(p.id);
    });
  });
  return map;
}

/**
 * Roles del profesional en la cirugía. Usa IDs si la cirugía los tiene; si no,
 * compara nombres (con las mismas variantes de nombre en todo el archivo).
 */
function rolesEnCirugia(surgery, professionalId, misNombres) {
  const roles = new Set();
  const idCirujano = surgery.mainSurgeonId || surgery.surgeonId || '';
  const idAnestesia = surgery.anesthesiologistId || '';
  if (idCirujano ? idCirujano === professionalId : misNombres.has(limpiarNombre(surgery.mainSurgeon))) {
    roles.add('cirujano');
  }
  if (idAnestesia ? idAnestesia === professionalId : misNombres.has(limpiarNombre(surgery.anesthesiologist))) {
    roles.add('anestesia');
  }
  return roles;
}

function protocoloAnestesiaLleno(protocol) {
  if (!protocol) return false;
  const p = protocol.anesthesiaProtocol || protocol;
  return !!(txt(p.plan) || txt(p.asaStatus) || txt(p.intraOpEvents) || txt(p.recovery));
}

function protocoloCirujanoLleno(protocol) {
  if (!protocol) return false;
  const p = protocol.surgeonProtocol || protocol;
  return !!(txt(p.postOpDiagnosis) || txt(p.description) || txt(p.findings) || txt(p.postOpPlan));
}

function fichaSoapCompleta(record) {
  const tieneMotivo = !!((record.motivoConsulta || '').trim ? (record.motivoConsulta || '').trim() : String(record.motivoConsulta || '').trim());
  const diags = record.diagnosticos;
  const tieneDiagnostico = Array.isArray(diags) ? diags.length > 0 : !!((diags || '').toString().trim());
  return tieneMotivo && tieneDiagnostico;
}

/**
 * Validador de fichas SOAP con consumo: cada ficha valida un solo acto, y se
 * aceptan fichas completadas hasta `tolerancia` días después del acto.
 */
function crearValidadorSOAP(records, professionalId, tolerancia) {
  const usadas = new Set();
  return function validar(act) {
    if (!act.patientId) {
      return { valido: false, razon: 'No se pudo identificar al paciente para verificar la ficha SOAP' };
    }
    const limite = addDays(act.date, tolerancia);
    const candidata = records
      .filter((r) => {
        if (usadas.has(r.id)) return false;
        if (r.vetId !== professionalId) return false;
        if (r.patientId !== act.patientId && r.petId !== act.patientId) return false;
        if (ESTADOS_FICHA_BORRADOR.includes(normalizeStr(r.status || r.estado))) return false;
        return r._fechas.some((f) => f >= act.date && f <= limite);
      })
      .sort((a, b) => a._fechas[0].localeCompare(b._fechas[0]))[0];
    if (!candidata) return { valido: false, razon: 'Falta ficha SOAP completada por el médico' };
    if (!fichaSoapCompleta(candidata)) {
      return { valido: false, razon: 'La ficha SOAP existe pero está incompleta (falta motivo o diagnóstico)' };
    }
    usadas.add(candidata.id);
    return { valido: true, razon: '' };
  };
}

// ─── Carga de presupuestos ──────────────────────────────────────────────────

async function cargarPresupuestosPorId(ids, advertencias) {
  const map = {};
  await Promise.all([...ids].map(async (id) => {
    try {
      const snap = await getDoc(doc(db, 'presupuestos', id));
      if (snap.exists()) map[id] = { id: snap.id, ...snap.data() };
      else advertencias.push(`El presupuesto ${id} referenciado no existe`);
    } catch (e) {
      advertencias.push(`Error leyendo presupuesto ${id}: ${e.message}`);
    }
  }));
  return map;
}

/** Busca presupuestos por número probando string y número (Firestore distingue tipos). */
async function cargarPresupuestosPorNumero(numeros, advertencias) {
  const valores = [];
  numeros.forEach((n) => {
    valores.push(String(n));
    const num = Number(n);
    if (Number.isFinite(num) && String(num) === String(n)) valores.push(num);
  });
  const resultado = [];
  for (const lote of chunk([...new Set(valores)], 10)) {
    try {
      const snap = await getDocs(query(collection(db, 'presupuestos'), where('numero', 'in', lote)));
      snap.docs.forEach((d) => resultado.push({ id: d.id, ...d.data() }));
    } catch (e) {
      advertencias.push(`Error buscando presupuestos por número: ${e.message}`);
    }
  }
  return resultado;
}

// ─── Liquidación ────────────────────────────────────────────────────────────

function getResumenVacio() {
  return {
    totalActos: 0,
    totalCobrado: 0,
    totalHonorarios: 0,
    totalHonorariosNoPagados: 0,
    actosConRegla: 0,
    actosSinRegla: 0,
    pagosConfirmados: 0,
    pagosPendientes: 0,
    actosValidados: 0,
    actosRechazados: 0,
    itemsPendientesRevision: 0,
    advertencias: 0,
  };
}

/**
 * Calcula la liquidación de un profesional en un rango de fechas.
 *
 * @param {string} professionalId
 * @param {string} fechaDesde - YYYY-MM-DD
 * @param {string} fechaHasta - YYYY-MM-DD
 * @param {object} [opciones] - ver OPCIONES_DEFAULT
 * @returns {Promise<{ items, resumen, pendientesRevision, advertencias }>}
 */
export async function calcularLiquidacionConContratos(professionalId, fechaDesde, fechaHasta, opcionesUsuario = {}, ajustes = []) {
  if (!professionalId) {
    return { items: [], resumen: getResumenVacio(), pendientesRevision: [], advertencias: [] };
  }
  if (!esFechaISO(fechaDesde) || !esFechaISO(fechaHasta) || fechaDesde > fechaHasta) {
    throw new Error('Rango de fechas inválido: use YYYY-MM-DD y fechaDesde <= fechaHasta');
  }
  const opciones = { ...OPCIONES_DEFAULT, ...opcionesUsuario };
  const advertencias = [];
  const pendientesRevision = [];

  // 1. Carga de datos (en paralelo)
  // TODO rendimiento: Sales y surgeries se leen completas; conviene consultarlas por rango de fechas.
  const [config, contratosSnap, professional, professionals, salesAll, surgeriesAll, bookingsSnap, clinicalSnap, imagingResultsSnap] =
    await Promise.all([
      getHonorarioConfig(professionalId),
      getDocs(query(collection(db, CONTRATOS_COLLECTION), where('professionalId', '==', professionalId))),
      getDoc(doc(db, 'professionals', professionalId)).then((s) => (s.exists() ? { id: s.id, ...s.data() } : null)),
      getCollection('professionals'),
      getCollection('Sales'),
      getCollection('surgeries'),
      getDocs(query(collection(db, 'onlineBookings'), where('professionalId', '==', professionalId))),
      getDocs(query(collection(db, 'clinicalRecords'), where('vetId', '==', professionalId))),
      getDocs(query(collection(db, 'imagingResults'), where('radiologistId', '==', professionalId))),
    ]);

  const rules = (config.rules || []).map(normalizarRegla);
  const contratos = contratosSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  if (!professional) advertencias.push('No se encontró la ficha del profesional; no se pueden identificar sus cirugías por nombre.');
  const misNombres = nombresProfesional(professional);
  const profNameMap = buildProfNameMap(professionals);
  misNombres.forEach((n) => {
    const ids = profNameMap[n];
    if (ids && ids.size > 1) {
      advertencias.push(`Hay ${ids.size} profesionales con el nombre "${n}"; las cirugías sin ID de médico pueden atribuirse mal.`);
    }
  });

  // Ventas no anuladas, con fecha de pago normalizada
  const ventasValidas = (salesAll || [])
    .map((s) => ({ ...s, _fecha: toDateStr(s.paidAt || s.createdAt), _estado: normalizeStr(s.estado) }))
    .filter((s) => !ESTADOS_VENTA_ANULADA.includes(s._estado));
  const ventasPorOrden = new Map();
  const ventasPorPresupuesto = new Map();
  const ventasById = new Map();
  ventasValidas.forEach((s) => {
    ventasById.set(s.id, s);
    if (s.orderId) {
      if (!ventasPorOrden.has(s.orderId)) ventasPorOrden.set(s.orderId, []);
      ventasPorOrden.get(s.orderId).push(s);
    }
    if (s.presupuestoId) {
      if (!ventasPorPresupuesto.has(s.presupuestoId)) ventasPorPresupuesto.set(s.presupuestoId, []);
      ventasPorPresupuesto.get(s.presupuestoId).push(s);
    }
  });

  // Citas del profesional (todas: el estado se evalúa abajo)
  const bookingsProf = bookingsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const bookingsById = new Map(bookingsProf.map((b) => [b.id, b]));
  const bookingsEnRango = bookingsProf.filter((b) => enRango(toDateStr(b.date), fechaDesde, fechaHasta));

  // Cirugías no canceladas; presupuestos quirúrgicos (por id Y por número)
  const cirugiasActivas = (surgeriesAll || [])
    .filter((s) => !ESTADOS_CIRUGIA_CANCELADA.includes(normalizeStr(s.status || s.estado)));
  const presupQuirurgicosIds = new Set(cirugiasActivas.map((s) => s.presupuestoId).filter(Boolean));
  const presupQuirurgicosNumeros = new Set(
    cirugiasActivas.map((s) => s.budgetNumber).filter((n) => n !== undefined && n !== null && n !== '').map(String),
  );
  const cirugiasProf = cirugiasActivas
    .map((s) => ({ ...s, _fecha: toDateStr(s.date), _roles: rolesEnCirugia(s, professionalId, misNombres) }))
    .filter((s) => enRango(s._fecha, fechaDesde, fechaHasta) && s._roles.size > 0)
    .sort((a, b) => a._fecha.localeCompare(b._fecha) || String(a.id).localeCompare(String(b.id)));

  // Ventas a considerar: las del rango + las de citas del rango (aunque se hayan pagado fuera)
  const ventasEnRango = ventasValidas.filter((s) => enRango(s._fecha, fechaDesde, fechaHasta));
  const ventasDeBookings = bookingsEnRango.flatMap((b) => ventasPorOrden.get(b.id) || []);

  // Presupuestos: por id y, para cirugías, por número como respaldo
  const idsPresupuesto = new Set([
    ...ventasEnRango.map((s) => s.presupuestoId),
    ...ventasDeBookings.map((s) => s.presupuestoId),
    ...cirugiasProf.map((s) => s.presupuestoId),
    ...bookingsEnRango.map((b) => b.presupuestoId),
  ].filter(Boolean));
  const presupuestosMap = await cargarPresupuestosPorId(idsPresupuesto, advertencias);
  const presupuestosPorNumero = {};
  Object.values(presupuestosMap).forEach((p) => {
    if (p.numero !== undefined && p.numero !== null) presupuestosPorNumero[String(p.numero)] = p;
  });
  const numerosFaltantes = [...new Set(cirugiasProf
    .filter((s) => !presupuestosMap[s.presupuestoId] && s.budgetNumber)
    .map((s) => String(s.budgetNumber))
    .filter((n) => !presupuestosPorNumero[n]))];
  if (numerosFaltantes.length) {
    const extra = await cargarPresupuestosPorNumero(numerosFaltantes, advertencias);
    extra.forEach((p) => {
      const key = String(p.numero);
      if (presupuestosPorNumero[key] && presupuestosPorNumero[key].id !== p.id) {
        advertencias.push(`El número de presupuesto ${key} está repetido en más de un documento`);
      }
      presupuestosPorNumero[key] = p;
      presupuestosMap[p.id] = p;
    });
  }

  // Protocolos de las cirugías del profesional (todos, no solo el primero)
  const protocolosPorCirugia = new Map();
  for (const lote of chunk(cirugiasProf.map((s) => s.id).filter(Boolean), 10)) {
    try {
      const snap = await getDocs(query(collection(db, 'surgeryProtocols'), where('surgeryId', 'in', lote)));
      snap.docs.forEach((d) => {
        const p = { id: d.id, ...d.data() };
        if (!protocolosPorCirugia.has(p.surgeryId)) protocolosPorCirugia.set(p.surgeryId, []);
        protocolosPorCirugia.get(p.surgeryId).push(p);
      });
    } catch (e) {
      advertencias.push(`Error leyendo protocolos quirúrgicos: ${e.message}. Algunas cirugías aparecerán sin protocolo.`);
    }
  }

  // Fichas SOAP con fechas normalizadas, dentro del rango + tolerancia
  const limiteSOAP = addDays(fechaHasta, opciones.toleranciaDiasSOAP);
  const fichas = clinicalSnap.docs
    .map((d) => {
      const r = { id: d.id, ...d.data() };
      r._fechas = [toDateStr(r.date), toDateStr(r.fecha), toDateStr(r.createdAt), toDateStr(r.updatedAt)].filter(Boolean).sort();
      return r;
    })
    .filter((r) => r._fechas.some((f) => f >= fechaDesde && f <= limiteSOAP));
  const validarSOAP = crearValidadorSOAP(fichas, professionalId, opciones.toleranciaDiasSOAP);

  // ── Construcción de filas ────────────────────────────────────────────────

  const filas = [];

  function crearFila({
    id, origen, act, rule, monto, montoBase, cantidad = 1, paciente, pagoConfirmado,
    pagable = pagoConfirmado, motivoNoPagable = '', notas = [], validar, extra = {},
  }) {
    const base = toNumber(montoBase ?? monto);
    const honorarioCalculado = rule
      ? calcularHonorarioItem(base, rule, { cantidad, fijoPorCantidad: opciones.fijoPorCantidad })
      : 0;
    filas.push({
      id,
      origen,
      ventaId: '',
      bookingId: '',
      presupuestoId: '',
      presupuestoNumber: '',
      surgeryId: '',
      rol: '',
      boleta: '',
      ...extra,
      fecha: act.date,
      time: act.time || '',
      paciente: paciente || '—',
      prestacion: act.prestacion,
      arancelId: act.arancelId,
      tipoPrestacionId: act.tipoPrestacionId,
      tipoPrestacionName: act.tipoPrestacionName,
      cantidad,
      montoCobrado: Math.round(toNumber(monto)),
      montoBase: Math.round(base),
      modalidad: rule?.modalidad || 'sin_regla',
      porcentaje: rule?.porcentaje || 0,
      montoFijo: rule?.montoFijo || 0,
      contratoId: rule?._contratoId || '',
      origenRegla: rule?._origen || '',
      honorarioCalculado,
      pagoConfirmado: !!pagoConfirmado,
      _validar: validar,
      _pagable: !!pagable,
      _motivoNoPagable: motivoNoPagable,
      _notas: notas,
    });
  }

  /** Validación para actos que no vienen de cirugía. */
  const validadorGeneral = (act, rule) => () => {
    const tipo = act.tipoPrestacionName || rule?.tipoPrestacionName || act.prestacion;
    if (esConsulta(tipo)) return validarSOAP(act);
    if (esHonorarioMedico(tipo)) {
      return { valido: false, razon: 'Honorario médico sin cirugía asociada (debe liquidarse desde la cirugía)' };
    }
    return { valido: true, razon: '' };
  };

  /** Decide si un ítem de venta es del profesional: 'propio' | 'ajeno' | 'desconocido'. */
  function atribuirItem({ saleItem, sale, presupuesto, booking, totalItemsVenta }) {
    const idItem = idProfesionalDe(saleItem);
    if (idItem) return idItem === professionalId ? 'propio' : 'ajeno';
    if (booking) {
      if (saleItem.arancelId && booking.arancelId && saleItem.arancelId === booking.arancelId) return 'propio';
      if (saleItem.tipoPrestacionId && booking.tipoPrestacionId
        && saleItem.tipoPrestacionId === booking.tipoPrestacionId) return 'propio';
      const tipoItem = normalizeStr(tipoDeItem(saleItem));
      if (tipoItem && tipoItem === normalizeStr(booking.tipoPrestacionName)) return 'propio';
      if (esConsulta(tipoItem)) return 'propio'; // la consulta cobrada en la venta de la cita
      if (totalItemsVenta === 1) return 'propio';
      return 'desconocido'; // p. ej. medicamentos vendidos junto a la consulta
    }
    const idVenta = idProfesionalDe(sale);
    if (idVenta) return idVenta === professionalId ? 'propio' : 'ajeno';
    if (opciones.atribuirPorPresupuesto && presupuesto) {
      const idPres = idProfesionalDe(presupuesto);
      if (idPres) return idPres === professionalId ? 'propio' : 'ajeno';
    }
    return 'desconocido';
  }

  function esVentaQuirurgica(sale, presupuesto) {
    if (sale.presupuestoId && presupQuirurgicosIds.has(sale.presupuestoId)) return true;
    if (presupuesto && presupuesto.numero !== undefined && presupQuirurgicosNumeros.has(String(presupuesto.numero))) return true;
    if (sale.budgetNumber && presupQuirurgicosNumeros.has(String(sale.budgetNumber))) return true;
    return false;
  }

  /** Procesa los ítems de una venta. Devuelve cuántos ítems quedaron atribuidos. */
  function procesarVenta(sale, { booking = null, fecha, time = '', notas = [] }) {
    const presupuesto = presupuestosMap[sale.presupuestoId] || null;
    const saleItems = sale.items || [];
    const quirurgica = esVentaQuirurgica(sale, presupuesto);
    const pagada = !ESTADOS_VENTA_PENDIENTE.includes(sale._estado);
    let atribuidos = 0;

    saleItems.forEach((saleItem, idx) => {
      // Los honorarios quirúrgicos se liquidan solo desde la cirugía (evita duplicarlos)
      if (quirurgica && esHonorarioMedico(tipoDeItem(saleItem))) return;

      const act = {
        date: fecha,
        time,
        branchId: sale.branchId || presupuesto?.branchId || booking?.branchId || '',
        clinicId: booking?.clinicId || sale.clinicId || '',
        specialtyId: saleItem.specialtyId || booking?.specialtyId || '',
        arancelId: saleItem.arancelId || '',
        tipoPrestacionId: saleItem.tipoPrestacionId || (booking && idx === 0 ? booking.tipoPrestacionId : '') || '',
        tipoPrestacionName: saleItem.tipoPrestacion || saleItem.tipoPrestacionName || '',
        prestacion: saleItem.descripcion || saleItem.prestacion || '',
        patientId: sale.patientId || presupuesto?.patientId || booking?.petId || booking?.patientId || '',
      };
      const rule = findRuleForAct(contratos, rules, act);
      if (!rule) return;

      const condiciones = rule._condiciones || CONDICIONES_DEFAULT;
      const factor = condiciones.prorratearDescuentos ? factorDescuento(sale, saleItems) : 1;
      const monto = montoItem(saleItem) * factor;
      const montoBase = montoBaseItem(monto, rule, presupuesto);

      const atribucion = atribuirItem({ saleItem, sale, presupuesto, booking, totalItemsVenta: saleItems.length });
      if (atribucion === 'ajeno') return;
      if (atribucion === 'desconocido') {
        if (rule?._origen === 'contrato') {
          pendientesRevision.push({
            ventaId: sale.id,
            itemIndex: idx,
            fecha,
            prestacion: act.prestacion || act.tipoPrestacionName,
            montoCobrado: Math.round(monto),
            contratoId: rule._contratoId,
            motivo: 'El ítem calza con un contrato del profesional pero no tiene profesional asignado',
          });
        }
        return;
      }

      atribuidos += 1;
      crearFila({
        id: `venta-${sale.id}-${idx}`,
        origen: booking ? 'cita' : 'venta',
        act,
        rule,
        monto,
        montoBase,
        cantidad: cantidadItem(saleItem),
        paciente: sale.patientName || booking?.petName || booking?.patientName || presupuesto?.patientName,
        pagoConfirmado: pagada,
        motivoNoPagable: pagada ? '' : `Venta en estado "${sale.estado}"`,
        notas,
        validar: validadorGeneral(act, rule),
        extra: {
          ventaId: sale.id,
          bookingId: booking?.id || '',
          presupuestoId: presupuesto?.id || '',
          presupuestoNumber: presupuesto?.numero ?? '',
          boleta: sale.boletaNumber || sale.folioDocumento || '',
        },
      });
    });
    return atribuidos;
  }

  // A) Citas del período: se liquidan en la fecha de la cita, con sus ventas (se hayan pagado cuando sea)
  bookingsEnRango.forEach((b) => {
    const fecha = toDateStr(b.date);
    const estado = normalizeStr(b.status);
    const ventasOrden = ventasPorOrden.get(b.id) || [];
    const ventasPres = b.presupuestoId ? ventasPorPresupuesto.get(b.presupuestoId) || [] : [];
    // Fallback: usar ventaId del presupuesto si no se encontró por orderId ni presupuestoId
    const presupCita = b.presupuestoId ? presupuestosMap[b.presupuestoId] : null;
    const ventaDirecta = presupCita?.ventaId && ventasById.has(presupCita.ventaId)
      ? [ventasById.get(presupCita.ventaId)]
      : [];
    const ventasVistas = new Set();
    const ventas = [...ventasOrden, ...ventasPres, ...ventaDirecta].filter((s) => {
      if (ventasVistas.has(s.id)) return false;
      ventasVistas.add(s.id);
      return true;
    });
    const completada = estado === 'completed';
    const realizada = completada || (ventas.length > 0 && !ESTADOS_BOOKING_NO_REALIZADO.includes(estado));
    if (!realizada) return;
    const notas = completada ? [] : [`Cita en estado "${b.status || 'sin estado'}" pero con pago registrado`];

    if (ventas.length) {
      const atribuidos = ventas.reduce((n, sale) => n + procesarVenta(sale, { booking: b, fecha, time: b.time || '', notas }), 0);
      if (!atribuidos) {
        advertencias.push(`La cita ${b.id} (${fecha}) tiene venta pero ningún ítem se pudo atribuir al profesional`);
      }
      return;
    }

    // Cita realizada sin venta: se muestra, pero no se paga hasta que exista el pago
    const itemCita = presupCita?.items?.[0] || null;
    const act = {
      date: fecha,
      time: b.time || '',
      branchId: b.branchId || '',
      clinicId: b.clinicId || '',
      specialtyId: b.specialtyId || '',
      arancelId: b.arancelId || itemCita?.arancelId || '',
      tipoPrestacionId: b.tipoPrestacionId || itemCita?.tipoPrestacionId || '',
      tipoPrestacionName: b.tipoPrestacionName || itemCita?.tipoPrestacion || b.specialtyName || '',
      prestacion: b.prestacion || itemCita?.prestacion || b.specialtyName || '',
      patientId: b.petId || b.patientId || '',
    };
    const rule = findRuleForAct(contratos, rules, act);
    if (!rule) return;
    const monto = toNumber(b.amount);
    const montoBase = montoBaseItem(monto, rule, null);
    crearFila({
      id: `cita-${b.id}`,
      origen: 'cita',
      act,
      rule,
      monto,
      montoBase,
      paciente: b.petName || b.patientName || b.ownerName,
      pagoConfirmado: false,
      motivoNoPagable: 'Pago pendiente: la cita no tiene venta asociada',
      validar: validadorGeneral(act, rule),
      extra: { bookingId: b.id },
    });
  });

  // B) Ventas del período que no son de citas del profesional
  ventasEnRango.forEach((sale) => {
    if (sale.orderId && bookingsById.has(sale.orderId)) return; // se liquida en la fecha de su cita (A)
    if (normalizeStr(sale.orderType) === 'onlinebooking') return; // cita de otro profesional
    procesarVenta(sale, { fecha: sale._fecha });
  });

  // C) Honorarios quirúrgicos: solo ítems de honorario del rol del profesional, una vez por ítem
  const itemsQuirurgicosUsados = new Set();
  cirugiasProf.forEach((s) => {
    const presupuesto = presupuestosMap[s.presupuestoId]
      || (s.budgetNumber ? presupuestosPorNumero[String(s.budgetNumber)] : null);
    const roles = [...s._roles];
    const baseAct = {
      date: s._fecha,
      time: s.startTime || '',
      branchId: presupuesto?.branchId || s.branchId || '',
      clinicId: s.clinicId || '',
      specialtyId: '',
      patientId: presupuesto?.patientId || s.patientId || '',
    };
    const paciente = presupuesto?.patientName || s.patientName;

    if (!presupuesto) {
      crearFila({
        id: `cir-${s.id}-sin-presupuesto`,
        origen: 'cirugia',
        act: { ...baseAct, arancelId: '', tipoPrestacionId: '', tipoPrestacionName: 'Honorarios Médicos', prestacion: '' },
        rule: null,
        monto: 0,
        paciente,
        pagoConfirmado: false,
        validar: () => ({ valido: false, razon: 'No se encontró presupuesto asociado a la cirugía' }),
        extra: { surgeryId: s.id, rol: roles.join(', ') },
      });
      return;
    }

    const presItems = presupuesto.items || [];
    const pagado = ESTADOS_PRESUPUESTO_PAGADO.includes(normalizeStr(presupuesto.estado));
    const protocolos = protocolosPorCirugia.get(s.id) || [];
    const rolesCubiertos = new Set();

    presItems.forEach((item, idx) => {
      if (!esHonorarioMedico(tipoDeItem(item))) return; // el resto del presupuesto no es honorario

      const idItem = idProfesionalDe(item);
      if (idItem && idItem !== professionalId) return;
      const rol = rolDeItemHonorario(item);

      const act = {
        ...baseAct,
        arancelId: item.arancelId || '',
        tipoPrestacionId: item.tipoPrestacionId || '',
        tipoPrestacionName: item.tipoPrestacion || item.tipoPrestacionName || '',
        prestacion: item.prestacion || item.descripcion || '',
      };
      const rule = findRuleForAct(contratos, rules, act);
      if (!rule) return;

      const condiciones = rule._condiciones || CONDICIONES_DEFAULT;
      const factor = condiciones.prorratearDescuentos ? factorDescuento(presupuesto, presItems) : 1;
      const pagable = pagado || !condiciones.exigirPagoEnCirugias;
      const monto = montoItem(item) * factor;
      const montoBase = montoBaseItem(monto, rule, presupuesto);

      const clave = `${presupuesto.id}#${idx}`;
      if (itemsQuirurgicosUsados.has(clave)) {
        advertencias.push(`El presupuesto ${presupuesto.numero ?? presupuesto.id} está asociado a más de una cirugía; su honorario se liquidó una sola vez`);
        return;
      }
      itemsQuirurgicosUsados.add(clave);
      rolesCubiertos.add(rol);

      crearFila({
        id: `cir-${s.id}-${presupuesto.id}-${idx}`,
        origen: 'cirugia',
        act,
        rule,
        monto,
        montoBase,
        cantidad: cantidadItem(item),
        paciente,
        pagoConfirmado: pagado,
        pagable,
        motivoNoPagable: pagable ? '' : 'Presupuesto aún no cobrado',
        validar: () => {
          if (!s._roles.has(rol)) {
            return {
              valido: false,
              razon: rol === 'anestesia'
                ? 'La prestación es de anestesista y el médico no cumple ese rol en la cirugía'
                : 'La prestación es del cirujano y el médico no cumple ese rol en la cirugía',
            };
          }
          if (!protocolos.length) return { valido: false, razon: 'La cirugía no tiene protocolo registrado' };
          if (rol === 'anestesia' && !protocolos.some(protocoloAnestesiaLleno)) {
            return { valido: false, razon: 'Falta Protocolo de Anestesia completado' };
          }
          if (rol === 'cirujano' && !protocolos.some(protocoloCirujanoLleno)) {
            return { valido: false, razon: 'Falta Protocolo del Cirujano completado' };
          }
          return { valido: true, razon: '' };
        },
        extra: {
          surgeryId: s.id,
          rol,
          presupuestoId: presupuesto.id,
          presupuestoNumber: presupuesto.numero ?? '',
          ventaId: presupuesto.ventaId || '',
          boleta: presupuesto.boletaNumber || presupuesto.folioDocumento || '',
        },
      });
    });

    roles.filter((r) => !rolesCubiertos.has(r)).forEach((r) => {
      advertencias.push(`Cirugía ${s.id} (${s._fecha}): el presupuesto no tiene ítem de honorario para el rol "${r}"`);
    });
  });

  // D) Honorarios radiológicos: se liquidan por el radiólogo que emitió el informe
  //    (radiologistId en imagingResults), no por quien solicitó la orden.
  const imagingResultsProf = imagingResultsSnap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((r) => {
      const fecha = toDateStr(r.approvedAt || r.reportedAt);
      return enRango(fecha, fechaDesde, fechaHasta);
    });

  if (imagingResultsProf.length) {
    // Cargar las imagingOrders correspondientes a los resultados
    const orderIds = [...new Set(imagingResultsProf.map((r) => r.orderId).filter(Boolean))];
    const imagingOrdersMap = new Map();
    await Promise.all(orderIds.map(async (oid) => {
      try {
        const snap = await getDoc(doc(db, 'imagingOrders', oid));
        if (snap.exists()) imagingOrdersMap.set(oid, { id: snap.id, ...snap.data() });
      } catch (e) { /* ignore individual errors */ }
    }));

    imagingResultsProf.forEach((result) => {
      const order = imagingOrdersMap.get(result.orderId);
      if (!order) {
        advertencias.push(`Resultado de imagen ${result.id}: no se encontró la orden ${result.orderId}`);
        return;
      }

      // Buscar la venta asociada a la orden de imagen
      const ventasOrden = ventasPorOrden.get(order.id) || [];
      const venta = ventasOrden[0]; // asume una venta por orden de imagen

      const fecha = toDateStr(result.approvedAt || result.reportedAt);
      const paciente = order.patientName || result.patientName || '—';
      const estudios = order.studiesRequested || [];
      const pagada = venta ? !ESTADOS_VENTA_PENDIENTE.includes(venta._estado) : false;
      // El clinicId de las ordenes de imagen suele ser "default" (fallback);
      // dejarlo vacío para que no bloquee el filtro de contrato por clinicId.
      const orderClinicId = order.clinicId && order.clinicId !== 'default' ? order.clinicId : '';
      const presupuestoId = order.presupuestoId || '';
      const presupuestoNumber = order.presupuestoNumber || '';

      // Si no hay estudios detallados, crear un acto genérico
      if (!estudios.length) {
        const act = {
          date: fecha,
          time: '',
          branchId: order.branchId || '',
          clinicId: orderClinicId,
          specialtyId: '',
          arancelId: '',
          tipoPrestacionId: '',
          tipoPrestacionName: 'Imágenes',
          prestacion: 'Estudio de imagen',
          patientId: order.patientId || '',
        };
        const rule = findRuleForAct(contratos, rules, act);
        const monto = venta ? toNumber(venta.total) : 0;
        const montoBase = montoBaseItem(monto, rule, null);
        crearFila({
          id: `imagen-${result.id}`,
          origen: 'imagen',
          act,
          rule,
          monto,
          montoBase,
          paciente,
          pagoConfirmado: pagada,
          motivoNoPagable: venta ? '' : 'Sin venta asociada',
          validar: () => ({ valido: true, razon: '' }),
          extra: {
            ventaId: venta?.id || '',
            imagingOrderId: order.id,
            imagingResultId: result.id,
            presupuestoId,
            presupuestoNumber,
          },
        });
        return;
      }

      // Por cada estudio, crear un acto independiente
      const saleItems = venta?.items || [];
      estudios.forEach((estudio, idx) => {
        const nombreEstudio = estudio.examTypeName || estudio.name || `Estudio ${idx + 1}`;
        // Buscar el item de la venta que coincida por nombre
        const saleItem = saleItems.find((it) =>
          normalizeStr(it.nombre || it.descripcion || it.prestacion || '') === normalizeStr(nombreEstudio)
        ) || saleItems[idx] || null;

        const act = {
          date: fecha,
          time: '',
          branchId: order.branchId || '',
          clinicId: orderClinicId,
          specialtyId: '',
          arancelId: saleItem?.arancelId || '',
          tipoPrestacionId: saleItem?.tipoPrestacionId || '',
          tipoPrestacionName: saleItem?.tipoPrestacion || saleItem?.tipoPrestacionName || 'Imágenes',
          prestacion: nombreEstudio,
          patientId: order.patientId || '',
        };
        const rule = findRuleForAct(contratos, rules, act);

        // Calcular monto: si hay item de venta, usarlo; si no, repartir el total entre los estudios
        let monto = 0;
        if (saleItem) {
          monto = montoItem(saleItem);
        } else if (venta && saleItems.length === 0) {
          monto = toNumber(venta.total) / estudios.length;
        }
        const montoBase = montoBaseItem(monto, rule, null);
        crearFila({
          id: `imagen-${result.id}-${idx}`,
          origen: 'imagen',
          act,
          rule,
          monto,
          montoBase,
          paciente,
          pagoConfirmado: pagada,
          motivoNoPagable: venta ? '' : 'Sin venta asociada',
          validar: () => ({ valido: true, razon: '' }),
          extra: {
            ventaId: venta?.id || '',
            imagingOrderId: order.id,
            imagingResultId: result.id,
            presupuestoId,
            presupuestoNumber,
          },
        });
      });
    });
  }

  // ── Validación en orden cronológico (las fichas SOAP se consumen en orden) ──

  filas.sort((a, b) => `${a.fecha} ${a.time} ${a.id}`.localeCompare(`${b.fecha} ${b.time} ${b.id}`));

  const items = filas.map((f) => {
    const { _validar, _pagable, _motivoNoPagable, _notas, ...fila } = f;
    const v = _validar ? _validar() : { valido: true, razon: '' };
    const obs = [..._notas];
    if (!v.valido) obs.push(v.razon);
    if (fila.modalidad === 'sin_regla') obs.push('Sin regla de honorario aplicable');
    if (!_pagable && _motivoNoPagable) obs.push(_motivoNoPagable);
    return {
      ...fila,
      validado: v.valido,
      honorario: v.valido && _pagable ? fila.honorarioCalculado : 0,
      observacionValidacion: obs.join('; '),
    };
  });

  // ── Garantía mensual: si el total de honorarios de un contrato queda bajo
  //    el mínimo garantizado, se agrega un ajuste por la diferencia.
  contratos.forEach((c) => {
    if (c.status !== 'vigente') return;
    const condiciones = normalizeCondiciones(c.condiciones);
    const garantia = condiciones.garantiaMensual;
    if (!garantia) return;
    const suma = items
      .filter((i) => i.contratoId === c.id && i.validado)
      .reduce((s, i) => s + toNumber(i.honorario), 0);
    if (suma < garantia) {
      items.push({
        id: `garantia-${c.id}-${fechaHasta}`,
        origen: 'garantia',
        ventaId: '',
        presupuestoId: '',
        presupuestoNumber: '',
        surgeryId: '',
        contratoId: c.id,
        fecha: fechaHasta,
        time: '',
        paciente: '—',
        prestacion: 'Garantía mensual',
        arancelId: '',
        tipoPrestacionId: '',
        tipoPrestacionName: '',
        cantidad: 1,
        montoCobrado: 0,
        montoBase: 0,
        modalidad: 'garantia',
        porcentaje: 0,
        montoFijo: 0,
        honorario: Math.round(garantia - suma),
        honorarioCalculado: Math.round(garantia - suma),
        pagoConfirmado: false,
        validado: true,
        observacionValidacion: `Diferencia de garantía mensual (${garantia - suma})`,
      });
    }
  });

  // ── Ajustes manuales: adelantos, retenciones, clawbacks, notas de crédito ──
  (ajustes || []).forEach((a) => {
    const monto = toNumber(a.monto);
    if (!monto) return;
    const pagoConfirmado = a.pagoConfirmado !== false;
    items.push({
      id: `ajuste-${a.id || Math.random().toString(36).slice(2)}`,
      origen: 'ajuste',
      ventaId: '',
      presupuestoId: '',
      presupuestoNumber: '',
      surgeryId: '',
      contratoId: a.contratoId || '',
      fecha: a.fecha || fechaHasta,
      time: '',
      paciente: a.paciente || '—',
      prestacion: a.descripcion || a.tipo || 'Ajuste',
      arancelId: '',
      tipoPrestacionId: '',
      tipoPrestacionName: '',
      cantidad: 1,
      montoCobrado: 0,
      montoBase: 0,
      modalidad: a.tipo || 'ajuste',
      porcentaje: 0,
      montoFijo: 0,
      honorario: Math.round(monto),
      honorarioCalculado: Math.round(monto),
      pagoConfirmado,
      validado: true,
      observacionValidacion: `Ajuste manual: ${a.tipo || ''}${a.descripcion ? ' - ' + a.descripcion : ''}`,
    });
  });

  const resumen = {
    totalActos: items.length,
    totalCobrado: items.reduce((s, i) => s + toNumber(i.montoCobrado), 0),
    totalHonorarios: items.reduce((s, i) => s + toNumber(i.honorario), 0),
    totalHonorariosNoPagados: items.reduce((s, i) => s + (toNumber(i.honorarioCalculado) - toNumber(i.honorario)), 0),
    actosConRegla: items.filter((i) => i.modalidad !== 'sin_regla').length,
    actosSinRegla: items.filter((i) => i.modalidad === 'sin_regla').length,
    pagosConfirmados: items.filter((i) => i.pagoConfirmado).length,
    pagosPendientes: items.filter((i) => !i.pagoConfirmado).length,
    actosValidados: items.filter((i) => i.validado).length,
    actosRechazados: items.filter((i) => !i.validado).length,
    itemsPendientesRevision: pendientesRevision.length,
    advertencias: advertencias.length,
  };

  return { items, resumen, pendientesRevision, advertencias: [...new Set(advertencias)] };
}

/**
 * @deprecated Usaba reglas distintas (sin validaciones, sobre el total de la venta).
 * Se mantiene por compatibilidad y delega en calcularLiquidacionConContratos para
 * que todas las pantallas muestren las mismas cifras.
 */
export async function calcularLiquidacion(professionalId, fechaDesde, fechaHasta, ajustes = []) {
  return calcularLiquidacionConContratos(professionalId, fechaDesde, fechaHasta, {}, ajustes);
}

// ─── Ajustes ───────────────────────────────────────────────────────────────

export async function getHonorarioAjustes(professionalId = '', fechaDesde = '', fechaHasta = '') {
  const ref = collection(db, AJUSTES_COLLECTION);
  const conditions = [];
  if (professionalId) conditions.push(where('professionalId', '==', professionalId));
  if (fechaDesde) conditions.push(where('fecha', '>=', fechaDesde));
  if (fechaHasta) conditions.push(where('fecha', '<=', fechaHasta));
  const snap = await getDocs(conditions.length ? query(ref, ...conditions) : ref);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

function normalizeAjusteData(data, { parcial = false } = {}) {
  const out = { ...data };
  delete out.id;
  delete out.createdAt;
  out.professionalId = data.professionalId || '';
  out.tipo = data.tipo || 'otro';
  out.monto = toNumber(data.monto);
  out.fecha = data.fecha || toDateStr(new Date());
  out.descripcion = data.descripcion || '';
  out.pagoConfirmado = data.pagoConfirmado !== false;
  out.contratoId = data.contratoId || '';
  out.paciente = data.paciente || '';
  Object.keys(out).forEach((k) => { if (out[k] === undefined) delete out[k]; });
  out.updatedAt = serverTimestamp();
  return out;
}

export async function createHonorarioAjuste(data) {
  if (!data?.professionalId) throw new Error('professionalId es requerido');
  const payload = normalizeAjusteData(data);
  payload.createdAt = serverTimestamp();
  const ref = await addDoc(collection(db, AJUSTES_COLLECTION), payload);
  return { id: ref.id, ...payload };
}

export async function updateHonorarioAjuste(id, data) {
  if (!id) throw new Error('id es requerido');
  await updateDoc(doc(db, AJUSTES_COLLECTION, id), normalizeAjusteData(data, { parcial: true }));
  const snap = await getDoc(doc(db, AJUSTES_COLLECTION, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function deleteHonorarioAjuste(id, { userId = '' } = {}) {
  if (!id) throw new Error('id es requerido');
  await updateDoc(doc(db, AJUSTES_COLLECTION, id), {
    status: 'eliminado',
    active: false,
    deletedAt: serverTimestamp(),
    deletedBy: userId,
    updatedAt: serverTimestamp(),
  });
}

// ─── Liquidaciones ─────────────────────────────────────────────────────────

export async function getHonorarioLiquidaciones(professionalId = '', periodo = '') {
  const ref = collection(db, LIQUIDACIONES_COLLECTION);
  const conditions = [];
  if (professionalId) conditions.push(where('professionalId', '==', professionalId));
  if (periodo) conditions.push(where('periodo', '==', periodo));
  const snap = await getDocs(conditions.length ? query(ref, ...conditions) : ref);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
}

export async function saveHonorarioLiquidacion(data, ajustes = []) {
  if (!data?.professionalId || !data?.periodo) throw new Error('professionalId y periodo son requeridos');
  const { professionalId, periodo, fechaDesde, fechaHasta } = data;
  const liquidacion = await calcularLiquidacionConContratos(professionalId, fechaDesde, fechaHasta, {}, ajustes);
  const payload = {
    professionalId,
    periodo,
    fechaDesde,
    fechaHasta,
    items: liquidacion.items,
    resumen: liquidacion.resumen,
    ajustes: (ajustes || []).map((a) => a.id).filter(Boolean),
    estado: data.estado || 'borrador',
    createdAt: data.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: data.createdBy || '',
    approvedByAdmin: data.approvedByAdmin || '',
    approvedAtAdmin: data.approvedAtAdmin || null,
    approvedByMedico: data.approvedByMedico || '',
    approvedAtMedico: data.approvedAtMedico || null,
    pagoAprobadoBy: data.pagoAprobadoBy || '',
    pagoAprobadoAt: data.pagoAprobadoAt || null,
    paidBy: data.paidBy || '',
    paidAt: data.paidAt || null,
    boletaHonorarios: data.boletaHonorarios || null,
  };
  if (data.id) {
    await updateDoc(doc(db, LIQUIDACIONES_COLLECTION, data.id), payload);
    return { id: data.id, ...payload };
  }
  const ref = await addDoc(collection(db, LIQUIDACIONES_COLLECTION), payload);
  return { id: ref.id, ...payload };
}

export async function getHonorarioLiquidacionById(id) {
  if (!id) return null;
  const snap = await getDoc(doc(db, LIQUIDACIONES_COLLECTION, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export function recalcularResumenLiquidacion(items = []) {
  return {
    totalActos: items.length,
    totalCobrado: items.reduce((s, i) => s + toNumber(i.montoCobrado), 0),
    totalHonorarios: items.reduce((s, i) => s + toNumber(i.honorario), 0),
    totalHonorariosNoPagados: items.reduce((s, i) => s + (toNumber(i.honorarioCalculado) - toNumber(i.honorario)), 0),
    actosConRegla: items.filter((i) => i.modalidad !== 'sin_regla').length,
    actosSinRegla: items.filter((i) => i.modalidad === 'sin_regla').length,
    pagosConfirmados: items.filter((i) => i.pagoConfirmado).length,
    pagosPendientes: items.filter((i) => !i.pagoConfirmado).length,
    actosValidados: items.filter((i) => i.validado).length,
    actosRechazados: items.filter((i) => !i.validado).length,
  };
}

export async function updateHonorarioLiquidacion(id, data) {
  if (!id) throw new Error('id es requerido');
  const payload = { ...data, updatedAt: serverTimestamp() };
  ['id', 'createdAt'].forEach((k) => delete payload[k]);
  if (payload.items) {
    payload.resumen = recalcularResumenLiquidacion(payload.items);
  }
  await updateDoc(doc(db, LIQUIDACIONES_COLLECTION, id), payload);
  return getHonorarioLiquidacionById(id);
}

export async function sendLiquidacionToMedico(id, userId = '') {
  if (!id) throw new Error('id es requerido');
  await updateDoc(doc(db, LIQUIDACIONES_COLLECTION, id), {
    estado: 'aprobada_por_administrador',
    approvedByAdmin: userId,
    approvedAtAdmin: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function approveLiquidacionByMedico(id, userId = '') {
  if (!id) throw new Error('id es requerido');
  await updateDoc(doc(db, LIQUIDACIONES_COLLECTION, id), {
    estado: 'aprobada_por_medico',
    approvedByMedico: userId,
    approvedAtMedico: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function approvePagoHonorarios(id, userId = '') {
  if (!id) throw new Error('id es requerido');
  await updateDoc(doc(db, LIQUIDACIONES_COLLECTION, id), {
    estado: 'pago_aprobado',
    pagoAprobadoBy: userId,
    pagoAprobadoAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function markLiquidacionPagada(id, userId = '') {
  if (!id) throw new Error('id es requerido');
  await updateDoc(doc(db, LIQUIDACIONES_COLLECTION, id), {
    estado: 'pagada',
    paidBy: userId,
    paidAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteHonorarioLiquidacion(id) {
  if (!id) throw new Error('id es requerido');
  await deleteDoc(doc(db, LIQUIDACIONES_COLLECTION, id));
}

export async function uploadBoletaHonorarios(liquidacionId, file) {
  if (!liquidacionId || !file) throw new Error('liquidacionId y archivo son requeridos');
  const storageRef = ref(storage, `honorarioBoletas/${liquidacionId}/${file.name}`);
  await uploadBytes(storageRef, file);
  const url = await getDownloadURL(storageRef);
  return { url, path: storageRef.fullPath, name: file.name };
}

export function exportLiquidacionCSV(items) {
  if (!items?.length) return '';
  const headers = ['Fecha', 'Hora', 'Paciente', 'Presupuesto', 'Prestacion', 'Monto Cobrado', 'Honorario', 'Estado'];
  const rows = items.map((i) => [
    i.fecha,
    i.time || '',
    i.paciente || '',
    i.presupuestoNumber || '',
    i.prestacion || i.tipoPrestacionName || '',
    i.montoCobrado ?? 0,
    i.honorario ?? 0,
    i.pagoConfirmado ? 'Pagado' : 'Pendiente',
  ]);
  return [headers.join(','), ...rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
}
